import classNames from "classnames";
import { FC, useMemo } from "react";
import { useRecoilValue } from "recoil";

import { isEmpty, reverse } from "lodash";
import { usePathname } from "next/navigation";
import Skeleton from "react-loading-skeleton";
import InputDiscount from "~/components/InputDiscount";
import CouponItem from "~/components/Items/CouponItem";
import Text from "~/components/Text";
import Colors from "~/constants/colors";
import { useCart } from "~/data/cart";
import { CartAtom } from "~/services/cart";
import storeConfigAtom from "~/services/store-config/store-config.atom";
import { formatNumber } from "~/utils/number";
import styles from "./CheckoutSummary.module.scss";
import { capitalizeFirstLetter } from "~/utils/format";
import Icon from "~/components/Icon";

interface CheckoutSummaryProps {
  className?: string;
  showShipping?: boolean;
}

const CheckoutSummary: FC<CheckoutSummaryProps> = ({
  className,
  showShipping = true,
}) => {
  const pathname = usePathname();
  const cartSelector = useRecoilValue(CartAtom.cartSelector);
  // const { step_discount_config, step_discount_enable } = useRecoilValue(
  //   storeConfigAtom.store
  // );

  // const setCart = useSetRecoilState(CartAtom.cart);

  const { removeCouponCode } = useCart();

  const isCart = pathname.includes("/carts");

  const handleRemoveCoupon = () => {
    removeCouponCode({
      variables: {
        cartId: cartSelector.cartId,
      },
      // onCompleted(data:any) {
      //   const newCart:Partial<Cart> = data?.removeCouponFromCart?.cart
      //   setCart((prev) => ({
      //     ...prev,
      //     applied_coupons: newCart?.applied_coupons,
      //     prices:{
      //       ...prev.prices,
      //       discounts: newCart?.prices?.discounts
      //     }
      //    }))
      // }
    });
  };

  const displayData = useMemo(() => cartSelector, [isCart, cartSelector]);
  const discounts = useMemo(
    () => reverse([...(cartSelector?.prices?.discounts || [])]),
    [cartSelector]
  );
  // const stepDiscout: {
  //   discount: number;
  //   subtotal: number;
  // } = useMemo(() => {
  //   if (!step_discount_enable) return null;
  //   let subtotal = cartSelector?.subtotal;
  //   const index = step_discount_config.findLastIndex(
  //     (item) => subtotal >= item.subtotal
  //   );
  //   if (index === -1) return null;
  //   return step_discount_config[index];
  // }, [cartSelector, step_discount_config, step_discount_enable]);

  const loadingCheckingRemoveDiscount =
    !!cartSelector.loadingCheckingRemoveDiscount;

  return (
    <div className={classNames(styles.checkout_summary, className)}>
      <div className="flex items-center justify-between">
        <Text type="label-4" color={Colors.GREY_900}>
          Subtotal
        </Text>
        <Text type="body-3" color={Colors.GREY_800}>
          ${displayData?.subtotal.toFixed(2)}
        </Text>
      </div>
      <div className="flex items-center justify-between">
        <Text type="label-4" color={Colors.GREY_900}>
          Shipping
        </Text>
        <Text type="body-3" color={Colors.GREY_300}>
          {!isEmpty(cartSelector?.selected_shipping_method) &&
          !!cartSelector.shipping_methods?.length
            ? !cartSelector?.shipping_fee
              ? "Free Ship"
              : cartSelector?.shipping_fee?.toFixed(2)
            : "Calculated at checkout"}
        </Text>
      </div>
      <div className="flex flex-col gap-3">
        {!!discounts.length && (
          <>
            {discounts?.map((discount) => (
              <div className="flex items-center justify-between">
                <Text
                  type="label-4"
                  className="capitalize"
                  color={Colors.GREY_900}
                >
                  {capitalizeFirstLetter(discount.label || "Discount")}
                </Text>
                <div className="text-end">
                  <Text type="body-3" color={Colors.ACCENT_RED_50}>
                    {loadingCheckingRemoveDiscount ? (
                      <Skeleton width={50} />
                    ) : (
                      <>-${formatNumber(Number(discount.amount?.value), 2)}</>
                    )}
                  </Text>
                </div>
              </div>
            ))}
          </>
        )}

        {!displayData?.applied_coupons?.length && <InputDiscount />}

        <div className={styles.coupons}>
          {!loadingCheckingRemoveDiscount &&
            displayData?.applied_coupons?.map((couponItem) => (
              <CouponItem
                key={couponItem.code}
                coupon={couponItem.code}
                handleRemove={handleRemoveCoupon}
              />
            ))}
        </div>
      </div>
      <div className="flex justify-between items-center">
        <Text type="title-2" weight={600} color={Colors.GREY_900}>
          Total
        </Text>
        <Text type="title-3" color={Colors.PRIMARY}>
          {loadingCheckingRemoveDiscount ? (
            <Skeleton width={50} />
          ) : (
            <> ${formatNumber(displayData?.total, 2)}</>
          )}
        </Text>
      </div>
    </div>
  );
};

export default CheckoutSummary;
